import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { logout } from '../../utils/userJourneyHelper';

/**
 * API Health Guard - Monitors API call failures and forces re-authentication if needed
 * 
 * This component tracks consecutive API failures and can force logout/re-authentication
 * when it detects that the user appears authenticated but all API calls are failing.
 * This helps catch scenarios like:
 * - Token/audience mismatches
 * - Backend configuration issues  
 * - Expired or invalid tokens that passed initial validation
 * 
 * @param {Object} props
 * @param {ReactNode} props.children - Child components to render
 * @param {number} props.maxFailures - Max consecutive failures before forcing logout (default: 8)
 * @param {number} props.resetWindow - Time in ms to reset failure count (default: 60000)
 * @param {number} props.loginGracePeriod - Grace period after login in ms (default: 300000 = 5 min)
 */
const ApiHealthGuard = ({ 
  children, 
  maxFailures = 8, 
  resetWindow = 60000, // 1 minute
  loginGracePeriod = 300000 // 5 minutes grace period after login
}) => {
  const { user: [user] } = useContext(AuthContext);
  const [failureCount, setFailureCount] = useState(0);
  const [lastFailureTime, setLastFailureTime] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [loginTime, setLoginTime] = useState(null);

  // Track when user logs in
  useEffect(() => {
    if (user && !loginTime) {
      setLoginTime(Date.now());
      // Reset any existing failure count when user logs in
      setFailureCount(0);
      setLastFailureTime(null);
      setShowWarning(false);
    } else if (!user) {
      setLoginTime(null);
      setFailureCount(0);
      setLastFailureTime(null);
      setShowWarning(false);
    }
  }, [user, loginTime]);

  useEffect(() => {
    // Reset failure count after reset window
    if (lastFailureTime && Date.now() - lastFailureTime > resetWindow) {
      setFailureCount(0);
      setLastFailureTime(null);
      setShowWarning(false);
    }
  }, [lastFailureTime, resetWindow]);

  useEffect(() => {
    // Global API error listener
    const handleApiError = (event) => {
      const { status, url } = event.detail || {};
      
      // Only count 401/403 errors as auth failures
      if (status === 401 || status === 403) {
        // Check if we're still in the login grace period
        const isInGracePeriod = loginTime && (Date.now() - loginTime) < loginGracePeriod;
        
        if (isInGracePeriod) {
          // During grace period, only count failures more leniently
          console.warn('🟡 API Health Guard: Auth error during login grace period, not counting aggressively', {
            url,
            status,
            timeSinceLogin: Date.now() - loginTime
          });
          return; // Don't count failures during grace period
        }
        
        setFailureCount(prev => {
          const newCount = prev + 1;
          setLastFailureTime(Date.now());
          
          console.warn('🔴 API Health Guard: Auth failure counted', {
            count: newCount,
            maxFailures,
            url,
            status
          });
          
          // Show warning at 60% of max failures (instead of 50%)
          if (newCount >= Math.ceil(maxFailures * 0.6)) {
            setShowWarning(true);
          }
          
          // Force logout at max failures
          if (newCount >= maxFailures) {
            console.warn('🚨 API Health Guard: Too many authentication failures, forcing logout');
            setTimeout(() => {
              logout();
            }, 1000);
          }
          
          return newCount;
        });
      }
    };

    // Listen for API errors from apiHelper
    window.addEventListener('apiAuthError', handleApiError);
    
    return () => {
      window.removeEventListener('apiAuthError', handleApiError);
    };
  }, [maxFailures, loginTime, loginGracePeriod]);

  // Only render children if user exists
  if (!user) {
    return null;
  }

  // Calculate time since login for display
  const timeSinceLogin = loginTime ? Date.now() - loginTime : 0;
  const isInGracePeriod = timeSinceLogin < loginGracePeriod;

  return (
    <>
      {/* API Health Warning */}
      {showWarning && !isInGracePeriod && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: '#ff9800',
          color: 'white',
          padding: '8px 16px',
          textAlign: 'center',
          zIndex: 9999,
          fontSize: '14px'
        }}>
          ⚠️ Connection issues detected. You may need to refresh your session.
          {failureCount >= Math.ceil(maxFailures * 0.8) && (
            <button 
              onClick={logout}
              style={{
                marginLeft: '10px',
                padding: '4px 8px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                border: '1px solid white',
                color: 'white',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Refresh Session
            </button>
          )}
        </div>
      )}
      
      {children}
    </>
  );
};

export default ApiHealthGuard; 